import {
  differenceInCalendarDays,
  isValid,
  parse,
  parseISO,
  startOfDay,
} from 'date-fns';

import { IPraNotasCertificateAlert } from '../interfaces/IPraNotasCertificateAlert';

interface GetCertificateAlertParams {
  companyId?: string;
  certificateDueDate?: Date | string;
  baseDate?: Date;
}

function normalizeCertificateDueDate(
  certificateDueDate?: Date | string
): Date | null {
  if (!certificateDueDate) {
    return null;
  }

  const parsedDate =
    typeof certificateDueDate === 'string'
      ? (() => {
          const isoDate = parseISO(certificateDueDate);

          if (isValid(isoDate)) {
            return isoDate;
          }

          const brDate = parse(certificateDueDate, 'dd/MM/yyyy', new Date());

          if (isValid(brDate)) {
            return brDate;
          }

          return new Date(certificateDueDate);
        })()
      : new Date(certificateDueDate);

  if (!isValid(parsedDate)) {
    return null;
  }

  return startOfDay(parsedDate);
}

export function getCertificateAlert({
  companyId = '',
  certificateDueDate,
  baseDate = new Date(),
}: GetCertificateAlertParams): IPraNotasCertificateAlert | null {
  const normalizedDueDate = normalizeCertificateDueDate(certificateDueDate);

  if (!normalizedDueDate) {
    return null;
  }

  const daysUntilExpiration = differenceInCalendarDays(
    normalizedDueDate,
    startOfDay(baseDate)
  );

  let severity: IPraNotasCertificateAlert['severity'] = 'none';

  if (daysUntilExpiration <= 0) {
    severity = 'expired';
  } else if (daysUntilExpiration <= 7) {
    severity = 'critical';
  } else if (daysUntilExpiration <= 15) {
    severity = 'warning';
  } else if (daysUntilExpiration <= 30) {
    severity = 'info';
  }

  return {
    companyId,
    certificateDueDate: normalizedDueDate,
    daysUntilExpiration,
    severity,
    isExpired: daysUntilExpiration <= 0,
  };
}
